import * as ScrollArea from "@radix-ui/react-scroll-area";
import * as SelectPrimitive from "@radix-ui/react-select";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCloseOthersOnOpen } from "src/hooks/useCloseOthersOnOpen";
import { SudoButton } from "../SudoButton";
import { optionVariants } from "./shared";
import { renderSelectBody } from "./sharedSelectBody";

export type SelectVariant = "primary" | "secondary" | "tertiary" | "secondaryGrey" | "tertiaryGrey";
export type IconStatus = "default" | "success" | "danger";
export type OptionAlignment = "left" | "center";
export type Side = "bottom" | "top" | "right" | "left";
export type Align = "start" | "center" | "end";

export interface BaseOption {
  value: string;
  label: string;
  indentLevel?: number;
  subLabel?: string;
  tooltipText?: string;
  icon?: {
    label: string;
    src: string;
    status: IconStatus;
  };
  async?: {
    state: string;
    loadingPercentage: number;
    variant?: SelectVariant;
  };
}

export interface OptionGroup {
  isGroup: true;
  groupLabel: string;
  options: BaseOption[];
}

export type Option = BaseOption | OptionGroup;

export function findBaseOption(options: Option[], searchValue: string | undefined): BaseOption | undefined {
  if (!searchValue) return undefined;
  for (const option of options) {
    if ("isGroup" in option && option.isGroup) {
      const nested = option.options.find((subOption) => subOption.value === searchValue);
      if (nested) return nested;
      continue;
    }
    if (option.value === searchValue) {
      return option;
    }
  }
  return undefined;
}

type SudoButtonProps = React.ComponentProps<typeof SudoButton>;

interface ButtonSelectProps {
  options: Option[];
  label: string;
  value?: string;
  className?: string;
  maxHeight?: string;
  isOpen?: boolean;
  disabled?: boolean;
  side?: Side;
  align?: Align;
  onChange?: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  optionAlignment?: OptionAlignment;
  buttonProps?: SudoButtonProps; // forwarded to SudoButton trigger
}

const ButtonSelect = React.forwardRef<HTMLButtonElement, ButtonSelectProps>(
  (
    {
      className,
      label,
      options,
      value,
      disabled = false,
      onChange,
      maxHeight,
      isOpen = false,
      onOpenChange,
      optionAlignment = "left",
      side = "bottom",
      align = "start",
      buttonProps,
    },
    ref
  ) => {
    const [open, setOpen] = useState<boolean>(isOpen);
    const prevOpenRef = useRef<boolean>(open);
    const [selectedValue, setSelectedValue] = useState<string | undefined>(value);
    const [selectedOption, setSelectedOption] = useState<BaseOption | undefined>(findBaseOption(options, value));
    const onChangeRef = useRef(onChange);
    const onOpenChangeRef = useRef(onOpenChange);
    onChangeRef.current = onChange;
    onOpenChangeRef.current = onOpenChange;

    const { announceOpen } = useCloseOthersOnOpen("sudokit:select-open", () => setOpen(false));

    const handleOpenChange = useCallback((nextOpen: boolean): void => {
      setOpen(nextOpen);

      if (onOpenChangeRef.current) {
        onOpenChangeRef.current(nextOpen);
      }

      if (nextOpen) {
        announceOpen();
      } else if (value === undefined) {
        // Uncontrolled/action-menu usage can clear transient row highlighting on close.
        setSelectedValue(undefined);
      }
    }, [announceOpen, value]);

    useEffect(() => {
      // When open goes from true→false, wait for the next mouseup to blur.
      if (prevOpenRef.current === true && open === false) {
        const onMouseUp = (): void => {
          if (ref && "current" in ref && ref.current) {
            ref.current.blur();
          }
          window.removeEventListener("mouseup", onMouseUp);
        };
        window.addEventListener("mouseup", onMouseUp);
      }
      prevOpenRef.current = open;
    }, [open, ref]);

    const handleValueChange = useCallback((nextValue: string): void => {
      setSelectedValue(nextValue);

      if (onChangeRef.current) {
        onChangeRef.current(nextValue);
      }
    }, []);

    const selectedStateStyles = (option: BaseOption): string => {
      let styles = "";
      if (option.icon && option.icon.status) {
        styles += optionVariants({ variant: option.icon.status });
        styles += ` justify-start pr-4`;
      } else {
        styles += optionVariants({ variant: "default" });
      }

      return styles;
    };

    useEffect(() => {
      if (value === undefined) {
        setSelectedValue(undefined);
        setSelectedOption(undefined);
        return;
      }

      setSelectedValue(value);
      setSelectedOption(findBaseOption(options, value));
    }, [value, options]);

    useEffect(() => {
      if (selectedValue === undefined) {
        return;
      }

      const next = findBaseOption(options, selectedValue);
      if (!next) {
        return;
      }

      setSelectedOption(next);
    }, [selectedValue, options]);

    return (
      <SelectPrimitive.Root
        value={selectedOption ? selectedOption.value : ""}
        onValueChange={handleValueChange}
        disabled={disabled}
        open={open}
        // open={true}
        onOpenChange={handleOpenChange}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          className={`sudokit flex items-center h-[40px] font-inter border-0 fill-app-grey-0 [&>path]:fill-app-grey-0 text-base font-medium leading-[130%] border-solid before:rounded-[10px] px-0 py-0 text-left cursor-pointer ${
            className || ""
          }`}
          aria-label={label}
          asChild={true}
        >
          <SudoButton disabled={disabled} {...buttonProps}> {label}</SudoButton>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            avoidCollisions={true}
            position="popper"
            side={side}
            align={align}
            className={`z-[2147483647] origin-top animate-flyout bg-app-menu-color shadow-card border border-app-menu-color-hover p-1 rounded-xl ${
              !!maxHeight && maxHeight !== "auto" && maxHeight !== "100%"
                ? `overflow-y-scroll scrollbar-track-transparent scrollbar-thumb-[var(--scrollbar-thumb)] scrollbar-width-[1px]`
                : "max-h-full"
            }`}
            style={{ maxHeight: !!maxHeight ? maxHeight : "auto", minWidth: "var(--radix-select-trigger-width)" }}
          >
            <ScrollArea.Root className="h-full w-full" type="auto">
              <SelectPrimitive.Viewport className="bg-app-menu-color">
                <ScrollArea.Viewport className="h-full w-full">
                  {options.map((option) => {
                    if ("isGroup" in option && option.isGroup) {
                      return (
                        <React.Fragment key={`group-${option.groupLabel}`}>
                          <SelectPrimitive.Group key={`group-${option.groupLabel}-content`} className="mt-1">
                            <SelectPrimitive.Label
                              key={`${option.groupLabel}-label`}
                              className="pointer-events-none select-none text-left text-app-grey-1 font-inter text-xs uppercase tracking-[0.08em] px-3 py-1 mb-1"
                            >
                              {option.groupLabel}
                            </SelectPrimitive.Label>
                            {option.options.map((subOption) => (
                              <React.Fragment key={`${option.groupLabel}-${subOption.value}`}>
                                {renderSelectBody(subOption, selectedValue, selectedStateStyles, optionAlignment)}
                              </React.Fragment>
                            ))}
                          </SelectPrimitive.Group>
                          <hr className="border-app-menu-color-hover border-1 border-solid" />
                        </React.Fragment>
                      );
                    }

                    return renderSelectBody(option as BaseOption, selectedValue, selectedStateStyles, optionAlignment);
                  })}
                </ScrollArea.Viewport>
              </SelectPrimitive.Viewport>
              <ScrollArea.Scrollbar className="ScrollAreaScrollbar" orientation="vertical">
                <ScrollArea.Thumb className="ScrollAreaThumb" />
              </ScrollArea.Scrollbar>
            </ScrollArea.Root>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    );
  }
);

ButtonSelect.displayName = "ButtonSelect";

export default ButtonSelect;
